import React from 'react';
import { useImmerReducer } from 'use-immer';
import Autocomplete from '../components/Autocomplete';
import {
	Box,
	HStack,
	Flex,
	Heading,
	Input,
	Switch,
	Text,
	Textarea,
	Button,
	Accordion,
	NumberInput,
	CloseButton,
	Field,
	Portal,
	Dialog
} from '@chakra-ui/react';
import theme from '../theme';
import { roles } from '../lib/generics';
import SignaturePad from '../components/SignaturePad';
import MultiRef from 'react-multi-ref';

const getCrewMembers = ({ employee_crew_members = [], non_employee_crew_members = [], crew_members = [], crew = [] }) =>
	[...employee_crew_members, ...non_employee_crew_members, ...crew_members]
		.filter(
			cm =>
				cm.employee_crew_member_name ||
				cm.non_employee_crew_member_name ||
				cm.crew_member_name ||
				cm.employee_crew_member_role ||
				cm.non_employee_crew_member_role ||
				cm.crew_member_role
		)
		.map(cm => {
			cm.name = cm.employee_crew_member_name || cm.non_employee_crew_member_name || cm.crew_member_name;
			cm.role = cm.employee_crew_member_role || cm.non_employee_crew_member_role || cm.crew_member_role;

			const prevCm = crew.find(({ name }) => name.toLowerCase() === cm.name.toLowerCase());

			if (prevCm) {
				cm.signature = prevCm.signature ?? null;
				cm.physically_fit = prevCm.physically_fit ?? false;
				cm.ready_to_dive = prevCm.ready_to_dive ?? false;
			}

			return { ...cm };
		});

function formReducer(draft, action) {
	switch (action.type) {
		case 'update_field_value': {
			const { section, field, value } = action.payload;

			if (section) {
				draft.fields[section][field] = value;
			} else {
				draft.fields[field] = value;
			}

			break;
		}
		case 'add_orange_hat': {
			draft.fields.team_assessment.orange_hat_list.push({
				team_member: draft.fields.team_assessment.team_member,
				mentor: draft.fields.team_assessment.mentor
			});

			draft.fields.team_assessment.team_member = '';
			draft.fields.team_assessment.mentor = '';

			break;
		}
		case 'remove_orange_hat': {
			const { index } = action.payload;
			draft.fields.team_assessment.orange_hat_list.splice(index, 1);

			break;
		}
		case 'add_meeting_attendee': {
			draft.fields.attendees.push({
				name: '',
				signature: null,
				company_name: '',
				physically_fit: false
			});
			break;
		}
		case 'update_meeting_attendee': {
			const { field, index, value } = action.payload;
			draft.fields.attendees[index][field] = value;

			break;
		}
		case 'remove_meeting_attendee': {
			const { index } = action.payload;
			draft.fields.attendees.splice(index, 1);

			break;
		}
		case 'update_crew_member': {
			const { field, index, value } = action.payload;
			draft.fields.crew[index][field] = value;

			break;
		}
		case 'save_signature': {
			const { cmIndex, signatureData, crew = true } = action.payload;
			if (!crew) {
				const attendee = draft.fields.attendees[cmIndex];

				if (attendee) {
					attendee.signature = signatureData;
				}
			} else {
				const crewMember = draft.fields.crew[cmIndex];

				if (crewMember) {
					crewMember.signature = signatureData;
				}
			}

			break;
		}
		default:
			throw new Error();
	}
}

export default ({ currentForm, formData, handoffData, edit, saveForm, isSubmitted }) => {
	const [state, dispatch] = useImmerReducer(formReducer, {
		fields: {
			...formData,
			crew: getCrewMembers({ ...handoffData, crew: formData.crew }),
			hazards: {
				...formData.hazards,
				known_hazards: formData?.hazards?.known_hazards || handoffData.known_hazards || ''
			},
			emergency_procedures: {
				...formData.emergency_procedures,
				emergency_contacts: handoffData.emergency_contacts?.filter(
					emergency_contact =>
						emergency_contact.emergency_contact_name || emergency_contact.emergency_contact_phone_number
				),
				hospitals: handoffData.hospitals?.filter(
					hospital => hospital.name || hospital.hospital_phone_number || hospital.location
				),
				hospital_name: handoffData.hospital_name,
				hospital_phone_number: handoffData.hospital_phone_number,
				hospital_location: handoffData.hospital_location
			}
		}
	});

	const [removeOrangeHatsDialogOpen, setRemoveOrangeHatsDialogOpen] = React.useState(null);
	const [removeMeetingAttendeeDialogOpen, setRemoveMeetingAttendeeDialogOpen] = React.useState(null);
	const [removeTeamMemberAutocompleteDialogOpen, setRemoveTeamMemberAutocompleteDialogOpen] = React.useState(false);
	const [removeMentorAutocompleteDialogOpen, setRemoveMentorAutocompleteDialogOpen] = React.useState(false);

	const [orangeHatRefs] = React.useState(() => new MultiRef());
	const [meetingAttendeeRefs] = React.useState(() => new MultiRef());

	React.useEffect(() => {
		if (!state.fields.team_assessment.orange_hat_list) {
			dispatch({
				type: 'update_field_value',
				payload: {
					section: 'team_assessment',
					field: 'orange_hat_list',
					value: [
						state.fields.team_assessment.orange_hat_mentor
							? {
									team_member: '',
									mentor: state.fields.team_assessment.orange_hat_mentor
								}
							: null
					].filter(Boolean)
				}
			});
		}
	}, []);

	React.useEffect(() => {
		saveForm({
			...currentForm,
			form_data: state.fields
		});
	}, [state.fields]);

	return (
		<>
			<Box w="100%" mx="8px" mb="8px" mt="24px" p="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
				<Text fontSize={16} as="label" pb="4px">
					Description of Work Performed
				</Text>
				<Field.Root invalid={!state.fields.todays_tasks}>
					<Textarea
						fontSize={16}
						bgColor={theme.colors.white}
						value={state.fields.todays_tasks}
						onChange={({ target }) => {
							dispatch({
								type: 'update_field_value',
								payload: {
									field: 'todays_tasks',
									value: target.value
								}
							});
						}}
						disabled={isSubmitted && !edit}
						_disabled={{
							opacity: 1,
							bgColor: theme.colors.gray[400],
							color: theme.colors.black
						}}
					/>
				</Field.Root>
			</Box>
			<Accordion.Root multiple collapsible p="8px" defaultValue={[0, 1, 2, 3, 4]}>
				<Accordion.Item value={0} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Team
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							{state.fields.crew?.map((cm, cmIndex) => (
								<Flex key={`${cm.name}_${cmIndex}`}>
									<Box w="100%" m="8px" p="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text fontSize={16} as="label" pb="4px">
											Name
										</Text>
										<Text fontSize={16}>{cm.name}</Text>
									</Box>
									<Box w="100%" m="8px" p="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text fontSize={16} as="label" pb="4px">
											Role
										</Text>
										<Text fontSize={16}>{roles[cm.role]}</Text>
									</Box>
								</Flex>
							))}
							<Heading
								fontSize={24}
								as="label"
								pt="24px"
								pb="12px"
								color={theme.colors.blue[700]}
								px="8px"
							>
								Supervisor Assessment of Team Members
							</Heading>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'team_assessment',
													field: 'able_to_perform_duties',
													value: !state.fields.team_assessment.able_to_perform_duties
												}
											})
										}
										checked={state.fields.team_assessment.able_to_perform_duties}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Team Members fit to perform duties
									</Text>
								</HStack>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Restrictions
									</Text>
									<Input
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.team_assessment.restrictions}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'team_assessment',
													field: 'restrictions',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'team_assessment',
													field: 'orange_hats',
													value: !state.fields.team_assessment.orange_hats
												}
											})
										}
										checked={state.fields.team_assessment.orange_hats}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Do you have any Orange Hats on your Team?
									</Text>
								</HStack>
							</Flex>
							{state.fields.team_assessment.orange_hats ? (
								<>
									<Flex
										direction="column"
										m="8px"
										bgColor={theme.colors.gray[500]}
										borderRadius="base"
									>
										<Flex>
											<Text fontSize={16} w="100%" as="label" p="8px">
												Team Member Name
											</Text>
											<Text fontSize={16} w="100%" as="label" p="8px">
												Who is the onsite mentor assigned to them?
											</Text>
										</Flex>
										{state.fields.team_assessment.orange_hat_list?.map((orange_hat, index) => (
											<Flex key={index} align="center">
												<Text fontSize={16} w="100%" p="8px">
													{orange_hat.team_member}
												</Text>
												<Flex w="100%" alignItems="center">
													<Text fontSize={16} w="100%" p="8px">
														{orange_hat.mentor}
													</Text>
													{(!isSubmitted || edit) && (
														<>
															<CloseButton
																size="md"
																m="8px"
																color={theme.colors.white}
																bgColor={theme.colors.red[500]}
																_hover={{ background: theme.colors.red[600] }}
																disabled={isSubmitted && !edit}
																onClick={() => setRemoveOrangeHatsDialogOpen(index)}
															/>
															<Dialog.Root
																role="alertdialog"
																initialFocusEl={orangeHatRefs.map.get(index)}
																onOpenChange={({ open }) => {
																	if (!open) {
																		setRemoveOrangeHatsDialogOpen(null);
																	}
																}}
																open={removeOrangeHatsDialogOpen === index}
																placement="center"
															>
																<Portal>
																	<Dialog.Backdrop />
																	<Dialog.Positioner>
																		<Dialog.Content opacity="1 !important">
																			<Dialog.Header>
																				<Dialog.Title
																					fontSize="lg"
																					fontWeight="bold"
																				>
																					Confirm Delete
																				</Dialog.Title>
																			</Dialog.Header>
																			<Dialog.Body>
																				<p>
																					Are you sure you want to remove this
																					item?
																				</p>
																			</Dialog.Body>
																			<Dialog.Footer>
																				<Dialog.ActionTrigger asChild>
																					<Button
																						size="lg"
																						ref={orangeHatRefs.ref(index)}
																						onClick={() =>
																							setRemoveOrangeHatsDialogOpen(
																								null
																							)
																						}
																					>
																						Cancel
																					</Button>
																				</Dialog.ActionTrigger>
																				<Button
																					colorPalette="red"
																					size="lg"
																					ml={3}
																					bg={theme.colors.red[500]}
																					_hover={{
																						background:
																							theme.colors.red[600]
																					}}
																					color={theme.colors.white}
																					onClick={() => {
																						setRemoveOrangeHatsDialogOpen(
																							null
																						);
																						dispatch({
																							type: 'remove_orange_hat',
																							payload: { index }
																						});
																					}}
																				>
																					Delete Item
																				</Button>
																			</Dialog.Footer>
																			<Dialog.CloseTrigger asChild>
																				<CloseButton size="sm" />
																			</Dialog.CloseTrigger>
																		</Dialog.Content>
																	</Dialog.Positioner>
																</Portal>
															</Dialog.Root>
														</>
													)}
												</Flex>
											</Flex>
										))}
									</Flex>
									<Flex align="flex-end" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Flex w="100%" p="8px" flexDirection="column" justifyContent="space-between">
											<Dialog.Root
												role="alertdialog"
												closeOnEscape={false}
												closeOnInteractOutside={false}
												onOpenChange={({ open }) => {
													if (!open) {
														setRemoveTeamMemberAutocompleteDialogOpen(false);
													}
												}}
												open={removeTeamMemberAutocompleteDialogOpen}
												placement="center"
											>
												<Portal>
													<Dialog.Backdrop />
													<Dialog.Positioner>
														<Dialog.Content opacity="1 !important">
															<Dialog.Header>
																<Dialog.Title fontSize="lg" fontWeight="bold">
																	Could not find an employee match.
																</Dialog.Title>
															</Dialog.Header>
															<Dialog.Body>
																<p>
																	Please check that the name is spelled correctly. We
																	suggest you select a match from the autocomplete
																	list below the input. The employee will be removed,
																	please try adding again with a correct selection.
																</p>
															</Dialog.Body>
															<Dialog.Footer>
																<Button
																	colorPalette="red"
																	size="lg"
																	ml={3}
																	bg={theme.colors.red[500]}
																	_hover={{ background: theme.colors.red[600] }}
																	color={theme.colors.white}
																	onClick={() => {
																		setRemoveTeamMemberAutocompleteDialogOpen(
																			false
																		);
																		dispatch({
																			type: 'update_field_value',
																			payload: {
																				section: 'team_assessment',
																				field: 'team_member',
																				value: ''
																			}
																		});
																	}}
																>
																	Remove Item
																</Button>
															</Dialog.Footer>
														</Dialog.Content>
													</Dialog.Positioner>
												</Portal>
											</Dialog.Root>
											<Text fontSize={16} as="label" pb="4px">
												Team Member Name
											</Text>
											<Field.Root
												invalid={
													!state.fields.team_assessment.orange_hat_list?.length ||
													(state.fields.team_assessment.mentor &&
														(!state.fields.team_assessment.team_member ||
															state.fields.team_assessment.team_member === ''))
												}
											>
												<Autocomplete
													disabled={isSubmitted && !edit}
													dataList={window.employeeList}
													fieldName="name"
													value={state.fields.team_assessment.team_member}
													onChange={value => {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'team_assessment',
																field: 'team_member',
																value
															}
														});
													}}
													onBlur={() =>
														setRemoveTeamMemberAutocompleteDialogOpen(
															state.fields.team_assessment.team_member &&
																!window.employeeList.find(
																	({ name }) =>
																		name ===
																		state.fields.team_assessment.team_member
																)
														)
													}
													onSelected={value => {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'team_assessment',
																field: 'team_member',
																value
															}
														});
													}}
												/>
											</Field.Root>
										</Flex>
										<Flex w="100%" alignItems="flex-end">
											<Flex w="100%" p="8px" flexDirection="column">
												<Dialog.Root
													role="alertdialog"
													closeOnEscape={false}
													closeOnInteractOutside={false}
													onOpenChange={({ open }) => {
														if (!open) {
															setRemoveMentorAutocompleteDialogOpen(false);
														}
													}}
													open={removeMentorAutocompleteDialogOpen}
													placement="center"
												>
													<Portal>
														<Dialog.Backdrop />
														<Dialog.Positioner>
															<Dialog.Content opacity="1 !important">
																<Dialog.Header>
																	<Dialog.Title fontSize="lg" fontWeight="bold">
																		Could not find an employee match.
																	</Dialog.Title>
																</Dialog.Header>
																<Dialog.Body>
																	<p>
																		Please check that the name is spelled correctly.
																		We suggest you select a match from the
																		autocomplete list below the input. The employee
																		will be removed, please try adding again with a
																		correct selection.
																	</p>
																</Dialog.Body>
																<Dialog.Footer>
																	<Button
																		colorPalette="red"
																		size="lg"
																		ml={3}
																		bg={theme.colors.red[500]}
																		_hover={{ background: theme.colors.red[600] }}
																		color={theme.colors.white}
																		onClick={() => {
																			setRemoveMentorAutocompleteDialogOpen(
																				false
																			);
																			dispatch({
																				type: 'update_field_value',
																				payload: {
																					section: 'team_assessment',
																					field: 'mentor',
																					value: ''
																				}
																			});
																		}}
																	>
																		Remove Item
																	</Button>
																</Dialog.Footer>
															</Dialog.Content>
														</Dialog.Positioner>
													</Portal>
												</Dialog.Root>
												<Text fontSize={16} as="label" pb="4px">
													Who is the onsite mentor assigned to them?
												</Text>
												<Field.Root
													invalid={
														!state.fields.team_assessment.orange_hat_list?.length ||
														(state.fields.team_assessment.team_member &&
															(!state.fields.team_assessment.mentor ||
																state.fields.team_assessment.mentor === ''))
													}
												>
													<Autocomplete
														disabled={isSubmitted && !edit}
														dataList={window.employeeList}
														fieldName="name"
														value={state.fields.team_assessment.mentor}
														onChange={value => {
															dispatch({
																type: 'update_field_value',
																payload: {
																	section: 'team_assessment',
																	field: 'mentor',
																	value
																}
															});
														}}
														onBlur={() =>
															setRemoveMentorAutocompleteDialogOpen(
																state.fields.team_assessment.mentor &&
																	!window.employeeList.find(
																		({ name }) =>
																			name === state.fields.team_assessment.mentor
																	)
															)
														}
														onSelected={value => {
															dispatch({
																type: 'update_field_value',
																payload: {
																	section: 'team_assessment',
																	field: 'mentor',
																	value
																}
															});
														}}
													/>
												</Field.Root>
											</Flex>
											<Button
												size="lg"
												m="8px"
												color={theme.colors.white}
												bg={theme.colors.blue[700]}
												_hover={{ backgroundColor: theme.colors.blue[800] }}
												onClick={() => {
													if (
														state.fields.team_assessment.team_member &&
														state.fields.team_assessment.mentor
													) {
														dispatch({ type: 'add_orange_hat' });
													}
												}}
												disabled={isSubmitted && !edit}
											>
												Add
											</Button>
										</Flex>
									</Flex>
								</>
							) : null}
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={1} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Conditions
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Weather
									</Text>
									<Input
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.conditions?.weather}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'conditions',
													field: 'weather',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Air Temperature (°F)
									</Text>
									<NumberInput.Root
										value={state.fields.conditions?.temperature}
										onValueChange={e => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'conditions',
													field: 'temperature',
													value: e.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
									>
										<NumberInput.Input
											fontSize={16}
											bgColor={theme.colors.white}
											borderRadius="base"
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</NumberInput.Root>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={2} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Job Hazard Analysis
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Known Hazards
									</Text>
									<Field.Root invalid={!state.fields.hazards.known_hazards}>
										<Textarea
											fontSize={16}
											bgColor={theme.colors.white}
											value={state.fields.hazards.known_hazards}
											onChange={({ target }) => {
												dispatch({
													type: 'update_field_value',
													payload: {
														section: 'hazards',
														field: 'known_hazards',
														value: target.value
													}
												});
											}}
											disabled={isSubmitted && !edit}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</Field.Root>
								</Box>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Additional Hazards
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.hazards.other_hazards}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'other_hazards',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							<Heading
								fontSize={24}
								as="label"
								pt="24px"
								pb="12px"
								color={theme.colors.blue[700]}
								px="8px"
							>
								Personal Protective Equipment
							</Heading>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'hard_hat',
													value: !state.fields.protective_equipment.hard_hat
												}
											})
										}
										checked={state.fields.protective_equipment.hard_hat}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Hard Hat
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'safety_glasses',
													value: !state.fields.protective_equipment.safety_glasses
												}
											})
										}
										checked={state.fields.protective_equipment.safety_glasses}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Safety Glasses
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'steel_toe_boots',
													value: !state.fields.protective_equipment.steel_toe_boots
												}
											})
										}
										checked={state.fields.protective_equipment.steel_toe_boots}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Steel Toe Boots
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'gloves',
													value: !state.fields.protective_equipment.gloves
												}
											})
										}
										checked={state.fields.protective_equipment.gloves}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Gloves
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'safety_vests',
													value: !state.fields.protective_equipment.safety_vests
												}
											})
										}
										checked={state.fields.protective_equipment.safety_vests}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										High Vis Vests
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'hearing_protection',
													value: !state.fields.protective_equipment.hearing_protection
												}
											})
										}
										checked={state.fields.protective_equipment.hearing_protection}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Hearing Protection
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'pfd',
													value: !state.fields.protective_equipment.pfd
												}
											})
										}
										checked={state.fields.protective_equipment?.pfd || false}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										PFD
									</Text>
								</HStack>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Other
									</Text>
									<Input
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.protective_equipment.other}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'protective_equipment',
													field: 'other',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							<Heading
								fontSize={24}
								as="label"
								pt="24px"
								pb="12px"
								color={theme.colors.blue[700]}
								px="8px"
							>
								Topics to Discuss
							</Heading>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'hyperthermia_hypothermia',
													value: !state.fields.hazards.hyperthermia_hypothermia
												}
											})
										}
										checked={state.fields.hazards.hyperthermia_hypothermia}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Hyperthermia/Hypothermia
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'excavations_earthwork_activities',
													value: !state.fields.hazards.excavations_earthwork_activities
												}
											})
										}
										checked={state.fields.hazards.excavations_earthwork_activities}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Excavations/Earthwork Activities
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'confined_space_entry',
													value: !state.fields.hazards.confined_space_entry
												}
											})
										}
										checked={state.fields.hazards.confined_space_entry}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Confined Space Entry
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'fuel_containers_leaks',
													value: !state.fields.hazards.fuel_containers_leaks
												}
											})
										}
										checked={state.fields.hazards.fuel_containers_leaks}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Fuel Containers/Leaks
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'mechanical_deficiencies',
													value: !state.fields.hazards.mechanical_deficiencies
												}
											})
										}
										checked={state.fields.hazards.mechanical_deficiencies}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Mechanical Deficiencies
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'loss_of_conciousness',
													value: !state.fields.hazards.loss_of_conciousness
												}
											})
										}
										checked={state.fields.hazards.loss_of_conciousness}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Loss of Consciousness
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'fall_protection',
													value: !state.fields.hazards.fall_protection
												}
											})
										}
										checked={state.fields.hazards.fall_protection}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Fall Protection
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'overhead',
													value: !state.fields.hazards.overhead
												}
											})
										}
										checked={state.fields.hazards.overhead}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Overhead
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'slips_trips_falls',
													value: !state.fields.hazards.slips_trips_falls
												}
											})
										}
										checked={state.fields.hazards.slips_trips_falls}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Slips, Trips, and Falls
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'entrapment_entanglement_pinch',
													value: !state.fields.hazards.entrapment_entanglement_pinch
												}
											})
										}
										checked={state.fields.hazards.entrapment_entanglement_pinch}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Entrapment/Entanglement/Pinch
									</Text>
								</HStack>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Tools
									</Text>
									<Input
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.hazards.tools}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'tools',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Other
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.hazards.other}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'hazards',
													field: 'other',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={3} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Emergency Procedures
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_procedures',
													field: 'first_aid_kit',
													value: !state.fields.emergency_procedures.first_aid_kit
												}
											})
										}
										checked={state.fields.emergency_procedures.first_aid_kit}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										First Aid Kit
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_procedures',
													field: 'fire_extinguishers',
													value: !state.fields.emergency_procedures.fire_extinguishers
												}
											})
										}
										checked={state.fields.emergency_procedures.fire_extinguishers}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Fire Extinguishers
									</Text>
								</HStack>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_procedures',
													field: 'emergency_action_plan',
													value: !state.fields.emergency_procedures.emergency_action_plan
												}
											})
										}
										checked={state.fields.emergency_procedures.emergency_action_plan}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Emergency Action Plan
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_procedures',
													field: 'operational_emergency',
													value: !state.fields.emergency_procedures.operational_emergency
												}
											})
										}
										checked={state.fields.emergency_procedures.operational_emergency}
										disabled={isSubmitted && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Operational Emergency
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Emergency Notes
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields.emergency_procedures.emergency_notes}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'emergency_procedures',
													field: 'emergency_notes',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							{state.fields.emergency_procedures.emergency_contacts?.map((contact, index) => (
								<Flex key={index} w="100%">
									<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text fontSize={16} as="label" pb="4px">
											Name
										</Text>
										<Text fontSize={16}>{contact.emergency_contact_name}</Text>
									</Box>
									<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text fontSize={16} as="label" pb="4px">
											Phone Number
										</Text>
										<Text fontSize={16}>{contact.emergency_contact_phone_number}</Text>
									</Box>
								</Flex>
							))}
							{state.fields.emergency_procedures?.hospitals?.length ? (
								state.fields.emergency_procedures.hospitals.map(hospital => (
									<>
										<Flex>
											<Box
												w="100%"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Text fontSize={16} as="label" pb="4px">
													Hospital Name
												</Text>
												<Text fontSize={16}>{hospital.hospital_name}</Text>
											</Box>
											<Box
												w="100%"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Text fontSize={16} as="label" pb="4px">
													Hospital Phone Number
												</Text>
												<Text fontSize={16}>{hospital.hospital_phone_number}</Text>
											</Box>
										</Flex>
										<Flex>
											<Box
												w="100%"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Text fontSize={16} as="label" pb="4px">
													Hospital Location
												</Text>
												<Text fontSize={16}>{hospital.hospital_location}</Text>
											</Box>
										</Flex>
									</>
								))
							) : state.fields.emergency_procedures?.hospitals ? null : (
								<>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Hospital Name
											</Text>
											<Textarea
												fontSize={16}
												bgColor={theme.colors.white}
												value={state.fields.emergency_procedures.hospital_name}
												onChange={({ target }) => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'emergency_procedures',
															field: 'hospital_name',
															value: target.value
														}
													});
												}}
												disabled={isSubmitted && !edit}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</Box>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Hospital Phone Number
											</Text>
											<Textarea
												fontSize={16}
												bgColor={theme.colors.white}
												value={state.fields.emergency_procedures.hospital_phone_number}
												onChange={({ target }) => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'emergency_procedures',
															field: 'hospital_phone_number',
															value: target.value
														}
													});
												}}
												disabled={isSubmitted && !edit}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</Box>
									</Flex>
									<Flex>
										<Box
											w="100%"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Hospital Location
											</Text>
											<Input
												fontSize={16}
												bgColor={theme.colors.white}
												value={state.fields.emergency_procedures.hospital_location}
												onChange={({ target }) => {
													dispatch({
														type: 'update_field_value',
														payload: {
															section: 'emergency_procedures',
															field: 'hospital_location',
															value: target.value
														}
													});
												}}
												disabled={isSubmitted && !edit}
												_disabled={{
													opacity: 1,
													bgColor: theme.colors.gray[400],
													color: theme.colors.black
												}}
											/>
										</Box>
									</Flex>
								</>
							)}
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={4} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Meeting Attendees
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Button
								size="lg"
								disabled={isSubmitted && !edit}
								onClick={() => dispatch({ type: 'add_meeting_attendee' })}
								color={theme.colors.white}
								bg={theme.colors.blue[700]}
								_hover={{ backgroundColor: theme.colors.blue[800] }}
							>
								Add Attendee
							</Button>
							{state.fields.crew?.map((cm, cmIndex) => (
								<Flex key={`${cm.name}_${cmIndex}`}>
									<Flex w="100%" align="flex-start">
										<Box
											flex="2"
											p="8px"
											m="8px"
											bgColor={theme.colors.gray[500]}
											borderRadius="base"
										>
											<Text fontSize={16} as="label" pb="4px">
												Name
											</Text>
											<Text fontSize={16}>{cm.name}</Text>
										</Box>
										<Flex flex="1" minWidth="135px" maxWidth="135px">
											<Box
												flex="1"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Switch.Root
													colorPalette="blue"
													size="lg"
													pb="4px"
													onChange={() =>
														dispatch({
															type: 'update_crew_member',
															payload: {
																field: 'physically_fit',
																index: cmIndex,
																value: !cm.physically_fit
															}
														})
													}
													checked={cm.physically_fit}
													disabled={isSubmitted && !edit}
												>
													<Switch.HiddenInput />
													<Switch.Control />
												</Switch.Root>
												<Text fontSize={16} as="label" pl="4px">
													Physically Fit
												</Text>
											</Box>
										</Flex>
									</Flex>
									<Flex
										w="100%"
										m="8px"
										borderBottomWidth="2px"
										borderBottomColor={cm.signature ? 'transparent' : 'red'}
										wrap="wrap"
										alignItems="flex-end"
									>
										<SignaturePad
											disabled={isSubmitted && !edit}
											src={
												cm.signature ||
												'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
											}
											onSign={signatureData =>
												dispatch({
													type: 'save_signature',
													payload: {
														name: cm.name,
														cmIndex,
														signatureData
													}
												})
											}
										/>
									</Flex>
									<Flex minWidth="56px" />
								</Flex>
							))}
							{state.fields.attendees?.map((cm, cmIndex) => (
								<Flex key={`${cmIndex}`}>
									<Flex w="100%" align="flex-start">
										<Flex flex="2" direction="column">
											<Box
												flex="1"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Text fontSize={16} as="label" pb="4px">
													Name
												</Text>
												<Input
													fontSize={16}
													bgColor={theme.colors.white}
													value={cm.name}
													onChange={({ target }) =>
														dispatch({
															type: 'update_meeting_attendee',
															payload: {
																field: 'name',
																index: cmIndex,
																value: target.value
															}
														})
													}
													disabled={isSubmitted && !edit}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Box>
											<Box
												flex="1"
												p="8px"
												m="8px"
												bgColor={theme.colors.gray[500]}
												borderRadius="base"
											>
												<Text fontSize={16} as="label" pb="4px">
													Company Name
												</Text>
												<Input
													fontSize={16}
													bgColor={theme.colors.white}
													value={cm.company_name}
													onChange={({ target }) =>
														dispatch({
															type: 'update_meeting_attendee',
															payload: {
																field: 'company_name',
																index: cmIndex,
																value: target.value
															}
														})
													}
													disabled={isSubmitted && !edit}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Box>
										</Flex>
										<Flex flexDirection="column" minWidth="135px" maxWidth="135px" h="100%">
											<Box p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
												<Switch.Root
													colorPalette="blue"
													size="lg"
													pb="4px"
													onChange={() =>
														dispatch({
															type: 'update_meeting_attendee',
															payload: {
																field: 'physically_fit',
																index: cmIndex,
																value: !cm.physically_fit
															}
														})
													}
													checked={cm.physically_fit}
													disabled={isSubmitted && !edit}
												>
													<Switch.HiddenInput />
													<Switch.Control />
												</Switch.Root>
												<Text fontSize={16} as="label" pl="4px">
													Physically Fit
												</Text>
											</Box>
										</Flex>
									</Flex>
									<Flex
										w="100%"
										m="8px"
										borderBottomWidth="2px"
										borderBottomColor={cm.signature ? 'transparent' : 'red'}
										wrap="wrap"
										alignItems="flex-end"
									>
										<SignaturePad
											disabled={isSubmitted && !edit}
											src={
												cm.signature ||
												'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
											}
											onSign={signatureData =>
												dispatch({
													type: 'save_signature',
													payload: {
														name: cm.name,
														cmIndex,
														signatureData,
														crew: false
													}
												})
											}
										/>
									</Flex>
									{(window.isSupervisor && !isSubmitted) || edit ? (
										<Flex alignItems="center">
											<CloseButton
												size="md"
												m="0 8px"
												height="40px"
												width="40px"
												color={theme.colors.white}
												bgColor={theme.colors.red[500]}
												_hover={{ background: theme.colors.red[600] }}
												disabled={isSubmitted && !edit}
												onClick={() => setRemoveMeetingAttendeeDialogOpen(cmIndex)}
											/>
											<Dialog.Root
												role="alertdialog"
												initialFocusEl={meetingAttendeeRefs.map.get(cmIndex)}
												onOpenChange={({ open }) => {
													if (!open) {
														setRemoveMeetingAttendeeDialogOpen(null);
													}
												}}
												open={removeMeetingAttendeeDialogOpen === cmIndex}
												placement="center"
											>
												<Portal>
													<Dialog.Backdrop />
													<Dialog.Positioner>
														<Dialog.Content opacity="1 !important">
															<Dialog.Header>
																<Dialog.Title fontSize="lg" fontWeight="bold">
																	Confirm Delete
																</Dialog.Title>
															</Dialog.Header>
															<Dialog.Body>
																<p>Are you sure you want to remove this item?</p>
															</Dialog.Body>
															<Dialog.Footer>
																<Dialog.ActionTrigger asChild>
																	<Button
																		size="lg"
																		ref={meetingAttendeeRefs.ref(cmIndex)}
																		onClick={() =>
																			setRemoveMeetingAttendeeDialogOpen(null)
																		}
																	>
																		Cancel
																	</Button>
																</Dialog.ActionTrigger>
																<Button
																	colorPalette="red"
																	size="lg"
																	ml={3}
																	bg={theme.colors.red[500]}
																	color={theme.colors.white}
																	onClick={() => {
																		setRemoveMeetingAttendeeDialogOpen(null);
																		dispatch({
																			type: 'remove_meeting_attendee',
																			payload: { index: cmIndex }
																		});
																	}}
																>
																	Delete Item
																</Button>
															</Dialog.Footer>
															<Dialog.CloseTrigger asChild>
																<CloseButton size="sm" />
															</Dialog.CloseTrigger>
														</Dialog.Content>
													</Dialog.Positioner>
												</Portal>
											</Dialog.Root>
										</Flex>
									) : (
										<Flex minWidth="56px" />
									)}
								</Flex>
							))}
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
			</Accordion.Root>
		</>
	);
};
